import React from 'react';
import { stripSerial } from '../../utils/scan';
import { isOpen as rmaIsOpen, stageOf, RMA_OUTCOME, rmaActivityEntry } from '../rma/rmaHelpers';
import { subLocationsFor } from '../../utils/locations';

// Direct deploy — send devices from stock straight to a client site without a bench test.
//
// Replaces the old comma-separated-textarea modal. The point of the redesign: every serial is
// resolved against stock BEFORE anything moves, devices that cannot legally be deployed are held
// back and explained, and the compliance shortcut (marking untested units "Passed") is only asked
// about when it actually applies — and is then attributed and recorded.
//
// Why a component rather than inline in App.js: every setSt PUTs the whole state blob to Azure, so
// the compose-stage session state (serials, search text, reason) is kept local and written to st
// exactly once, at Deploy. This mirrors QCImportPanel's reasoning.

// Device states, in the order the blocker banner lists them. `phrase` is the mid-sentence wording —
// stored rather than lower-cased from `label` so acronyms keep their case ("on an RMA", not "on an rma").
const STATES = {
  ready:      { label: 'Ready',           phrase: 'ready',            blocks: false, tone: 'success' },
  untested:   { label: 'Never tested',    phrase: 'never tested',     blocks: false, tone: 'warning' },
  quarantine: { label: 'Quarantined',     phrase: 'quarantined',      blocks: true,  tone: 'danger'  },
  rma:        { label: 'On an RMA',       phrase: 'on an RMA',        blocks: true,  tone: 'danger'  },
  transfer:   { label: 'On another site',  phrase: 'moving from another site', blocks: false, tone: 'info' },
  deployed:   { label: 'Already on site', phrase: 'already on site',  blocks: true,  tone: 'danger'  },
  unknown:    { label: 'Not in stock',    phrase: 'not in stock',     blocks: true,  tone: 'neutral' },
};

const REASONS = [
  'Devices tested at the previous site and moved directly',
  'Urgent replacement — site is short of working units',
  'Supplier-sealed stock going straight to a client handover',
  'Other',
];

const TONE = {
  success: { fill: 'var(--success-fill)', text: 'var(--success-text)', border: 'var(--success-border)', row: 'transparent' },
  warning: { fill: 'var(--warning-fill)', text: 'var(--warning-text)', border: 'var(--warning-border)', row: 'var(--warning-fill)' },
  danger:  { fill: 'var(--danger-fill)',  text: 'var(--danger-text)',  border: 'var(--danger-border)',  row: 'var(--danger-fill)'  },
  info:    { fill: 'var(--info-fill)',    text: 'var(--info-text)',    border: 'var(--info-border)',    row: 'transparent' },
  neutral: { fill: 'var(--surface-inset)', text: 'var(--text-muted)',  border: 'var(--border-default)', row: 'var(--surface-inset)' },
};

const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function DirectDeployPanel({
  st, setSt, audit, account, getStats, locName, isSysLoc,
  onBack, onOpenLocation,
  requireBypassReason = true, allowUntestedDevices = true,
}) {
  const [stage, setStage] = React.useState('compose');
  const [locationId, setLocationId] = React.useState(null);
  // Optional sub-location within the destination. Only the manual ones are offered: a
  // portal project is the portal's to decide, so choosing one here would not stick.
  const [subLocation, setSubLocation] = React.useState('');
  const [locQuery, setLocQuery] = React.useState('');
  const [serials, setSerials] = React.useState([]);
  const [scanBuf, setScanBuf] = React.useState('');
  const [pasteBuf, setPasteBuf] = React.useState('');
  const [pasteOpen, setPasteOpen] = React.useState(false);
  const [reason, setReason] = React.useState(null);
  const [reasonNote, setReasonNote] = React.useState('');
  const [receipt, setReceipt] = React.useState(null);
  const [rmaOverride, setRmaOverride] = React.useState(false);
  const [rmaOverrideNote, setRmaOverrideNote] = React.useState('');
  const scanRef = React.useRef(null);
  const noteRef = React.useRef(null);

  React.useEffect(() => { if (stage === 'compose') scanRef.current?.focus(); }, [stage]);

  const sites = React.useMemo(
    () => (st.locations || []).filter(l => !isSysLoc(l) && (l.status || 'Active') !== 'Retired'),
    [st.locations, isSysLoc]
  );

  // Device count per site, keyed by location NAME — devices store their location as a name string.
  const countsByName = React.useMemo(() => {
    const m = {};
    (st.devices || []).forEach(d => {
      if (d.status === 'Retired') return;
      const k = (d.location || '').toLowerCase();
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [st.devices]);

  const siteCount = React.useCallback(l => countsByName[locName(l).toLowerCase()] || 0, [countsByName, locName]);

  // RMA position of each device on an open shipment: 'away' (still with the manufacturer, blocks)
  // or 'unbenched' (back on the bench but not tested since). An open shipment is not proof the
  // unit is away — returned_to_bench stays open until every unit is bench tested, and legacy
  // `returned` shipments sit there forever with no outcomes at all. So in that stage a unit only
  // counts as away when the shipment has outcomes and this unit has none (a partial return).
  const rmaByDevice = React.useMemo(() => {
    const m = new Map();
    (st.rmaShipments || []).filter(rmaIsOpen).forEach(sh => {
      const outcomes = sh.outcomes || {};
      const hasOutcomes = Object.keys(outcomes).length > 0;
      (sh.deviceIds || []).forEach(id => {
        let pos;
        if (stageOf(sh) !== 'returned_to_bench') pos = 'away';
        else if (!hasOutcomes) pos = 'unbenched';
        else if (!outcomes[id]?.outcome) pos = 'away';
        else if (RMA_OUTCOME[outcomes[id].outcome]?.needsBenchTest && !outcomes[id].benchTested) pos = 'unbenched';
        else return;
        if (m.get(id) !== 'away') m.set(id, pos);
      });
    });
    return m;
  }, [st.rmaShipments]);

  const destination = React.useMemo(
    () => sites.find(l => l.id === locationId) || null,
    [sites, locationId]
  );

  // Resolve one serial against stock. Returns the state, a human explanation, and the device.
  const resolve = React.useCallback((serial) => {
    const dev = (st.devices || []).find(d => (d.serial || '').toLowerCase() === serial.toLowerCase());
    if (!dev) return { serial, dev: null, state: 'unknown', why: 'No device with this serial is in the system — check the serial and scan it again.' };
    if (dev.status === 'Retired') return { serial, dev, state: 'unknown', why: 'This device has been retired and is no longer part of usable stock.' };
    const rmaPos = rmaByDevice.get(dev.id);
    if (rmaPos === 'away')
      return { serial, dev, state: 'rma', why: 'This unit is away on an open RMA and cannot be sent to site until it comes back.' };
    if (dev.status === 'Quarantine' || locName(dev.location) === 'Quarantine')
      return { serial, dev, state: 'quarantine', why: 'This unit is quarantined. It has to be cleared on the bench before it can go anywhere.' };
    const loc = locName(dev.location);
    if (loc && !isSysLoc(loc)) {
      // Already at the chosen destination — nothing to do, so it blocks rather than
      // writing a pointless same-site move into the device's history.
      if (destination && loc.toLowerCase() === locName(destination).toLowerCase())
        return { serial, dev, state: 'deployed', why: `This unit is already on ${loc} — it does not need moving.` };
      // On a different site: a site-to-site transfer. Moving it here takes it off the old
      // site in the same step, because a device's location is a single value.
      return { serial, dev, state: 'transfer', from: loc, why: `Currently on ${loc}. Deploying moves it here and takes it off ${loc}.` };
    }
    // A pass from before the RMA says nothing about the unit that came back.
    if (rmaPos === 'unbenched')
      return { serial, dev, state: 'untested', why: 'Back from RMA with no bench test since. Deploying now marks it passed without one.' };
    const stats = getStats(dev);
    if (!stats.tested) return { serial, dev, state: 'untested', why: 'No bench test on record. Deploying now marks it passed without one.' };
    if (stats.overall === 'fail') return { serial, dev, state: 'quarantine', why: `Last bench test failed${stats.failedChecks?.length ? ` on ${stats.failedChecks.join(', ')}` : ''}. Clear it on the bench first.` };
    return { serial, dev, state: 'ready', why: stats.last ? `Bench tested ${fmtDate(stats.last)} — passed.` : 'Bench tested and passed.' };
  }, [st.devices, rmaByDevice, destination, getStats, locName, isSysLoc]);

  const rows = React.useMemo(() => serials.map(resolve), [serials, resolve]);
  // An RMA hold can be overridden for the whole list, with a reason. The RMA record itself is
  // left alone, apart from an activity note, so whoever runs the RMA can see the unit went out.
  const rmaRows = rows.filter(r => r.state === 'rma');
  const overriding = rmaOverride && rmaRows.length > 0;
  const blocks = (r) => STATES[r.state].blocks && !(r.state === 'rma' && rmaOverride);
  const blocked = rows.filter(blocks);
  const overridden = overriding ? rmaRows : [];
  const untested = rows.filter(r => r.state === 'untested');
  const ready = rows.filter(r => r.state === 'ready');
  const transfers = rows.filter(r => r.state === 'transfer');
  const movable = ready.length + transfers.length + overridden.length + (allowUntestedDevices ? untested.length : 0);

  const addSerials = (raw) => {
    const toks = String(raw || '').split(/[\s,;]+/).map(t => stripSerial(t.trim())).filter(Boolean);
    if (!toks.length) return;
    setSerials(prev => {
      const seen = new Set(prev.map(s => s.toLowerCase()));
      const out = [...prev];
      toks.forEach(t => { if (!seen.has(t.toLowerCase())) { seen.add(t.toLowerCase()); out.push(t); } });
      return out;
    });
  };

  const needReason = untested.length > 0 && requireBypassReason;
  const reasonText = reason === 'Other' ? reasonNote.trim() : reason;
  const gates = [];
  if (!destination) gates.push('Choose a destination.');
  if (!movable) gates.push('Add at least one device that can move.');
  if (blocked.length) gates.push(`${plural(blocked.length, 'device', 'devices')} cannot be deployed — remove them to continue.`);
  if (needReason && !reasonText) gates.push('Give a reason for skipping the bench test.');
  if (overriding && !rmaOverrideNote.trim()) gates.push('Give a reason for overriding the RMA hold.');
  if (!allowUntestedDevices && untested.length) gates.push('Untested devices are not permitted by policy — remove them or run a bench test.');
  const canDeploy = gates.length === 0;

  const deploy = () => {
    if (!canDeploy) return;
    const moving = rows.filter(r => !blocks(r) && (allowUntestedDevices || r.state !== 'untested'));
    const movingIds = new Set(moving.map(r => r.dev.id));
    const dest = locName(destination);
    const now = new Date().toISOString();
    const by = account?.name || 'Unknown';
    const bypassed = moving.filter(r => r.state === 'untested');
    const rmaNote = rmaOverrideNote.trim();
    const overrideIds = new Set(overridden.map(r => r.dev.id));

    setSt(s => ({
      ...s,
      rmaShipments: overrideIds.size ? (s.rmaShipments || []).map(sh => {
        if (!rmaIsOpen(sh)) return sh;
        const mine = (sh.deviceIds || []).filter(id => overrideIds.has(id));
        if (!mine.length) return sh;
        const serials = overridden.filter(r => mine.includes(r.dev.id)).map(r => r.serial);
        return { ...sh, activity: [rmaActivityEntry('note', `Deployed to ${dest} under RMA override: ${serials.join(', ')} — ${rmaNote}`, by, now), ...(sh.activity || [])] };
      }) : s.rmaShipments,
      devices: s.devices.map(d => {
        if (!movingIds.has(d.id)) return d;
        const wasUntested = bypassed.some(r => r.dev.id === d.id);
        const wasOverridden = overrideIds.has(d.id);
        return {
          ...d,
          location: dest,
          // Undefined rather than '' when nothing was picked, so the field is absent on a
          // device with no sub-location rather than present and empty.
          subLocation: subLocation || undefined,
          status: 'Passed',
          maintenancePending: undefined,
          locationLog: [...(d.locationLog || []), {
            from: d.location, to: dest, date: now, by,
            ...(wasUntested ? { bypass: reasonText } : {}),
            ...(wasOverridden ? { rmaOverride: rmaNote } : {}),
          }],
        };
      }),
    }));

    const moved = moving.filter(r => r.state === 'transfer');
    audit('DIRECT_DEPLOY', {
      destination: dest, subLocation: subLocation || null, siteId: destination.siteId,
      deviceCount: moving.length, serials: moving.map(r => r.serial),
      untestedCount: bypassed.length,
      untestedSerials: bypassed.map(r => r.serial),
      bypassReason: bypassed.length ? reasonText : null,
      transferCount: moved.length,
      transfers: moved.map(r => ({ serial: r.serial, from: r.from })),
      rmaOverrideCount: overridden.length,
      rmaOverrideSerials: overridden.map(r => r.serial),
      rmaOverrideReason: overridden.length ? rmaNote : null,
      heldBack: blocked.length, by,
    });

    setReceipt({
      count: moving.length, dest, site: destination,
      onSiteAfter: siteCount(destination) + moving.length,
      held: blocked.length, bypassed: bypassed.length,
      reason: bypassed.length ? reasonText : null,
      transfers: moved.length,
      transferFrom: [...new Set(moved.map(r => r.from))],
      rmaOverridden: overridden.length, rmaReason: overridden.length ? rmaNote : null,
      by, at: now,
    });
    setStage('done');
  };

  const reset = () => {
    setStage('compose'); setLocationId(null); setLocQuery(''); setSerials([]); setSubLocation('');
    setScanBuf(''); setPasteBuf(''); setPasteOpen(false); setReason(null); setReasonNote(''); setReceipt(null);
    setRmaOverride(false); setRmaOverrideNote('');
  };

  // ── Receipt ──────────────────────────────────────────────────────────────
  if (stage === 'done' && receipt) {
    const facts = [
      ['Site', `${receipt.site.siteId || '—'}${receipt.site.address ? ` · ${receipt.site.address}` : ''}`],
      ['Deployed by', `${receipt.by} on ${fmtDate(receipt.at)}`],
      ['Marked passed without a test', receipt.bypassed ? plural(receipt.bypassed, 'device', 'devices') : 'None'],
      ['Reason recorded', receipt.reason || 'Not required'],
    ];
    if (receipt.rmaOverridden) facts.push(
      ['RMA hold overridden', plural(receipt.rmaOverridden, 'device', 'devices')],
      ['Override reason', receipt.rmaReason],
    );
    if (receipt.transfers) facts.splice(2, 0, [
      'Moved off other sites',
      `${plural(receipt.transfers, 'device', 'devices')} from ${receipt.transferFrom.join(', ')}`,
    ]);
    return (
      <div style={{ maxWidth: 760 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 14, padding: '20px 22px', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--success-fill)', border: '1px solid var(--success-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--success-text)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-default)', letterSpacing: '-.25px' }}>
                {plural(receipt.count, 'device', 'devices')} deployed to {receipt.site.customer ? `${receipt.site.customer} ` : ''}{receipt.dest}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                Stock has been moved and the location now shows {plural(receipt.onSiteAfter, 'device', 'devices')}.
                {receipt.transfers ? ` ${plural(receipt.transfers, 'unit was', 'units were')} taken off ${receipt.transferFrom.join(', ')}.` : ''}
                {receipt.held ? ` ${plural(receipt.held, 'held-back unit', 'held-back units')} stayed in stock.` : ''}
              </div>
            </div>
          </div>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 22px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{k}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-default)', textAlign: 'right' }}>{v}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', padding: '16px 22px' }}>
            <button className="btn btn-primary" onClick={reset}>Deploy to another location</button>
            <button className="btn btn-ghost" onClick={() => onOpenLocation?.(receipt.site)}>Open the location</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Compose ──────────────────────────────────────────────────────────────
  const q = locQuery.trim().toLowerCase();
  const matches = q
    ? sites.filter(l => `${l.customer || ''} ${locName(l)} ${l.address || ''} ${l.siteId || ''}`.toLowerCase().includes(q))
    : sites;
  const CAP = 6;
  const shown = matches.slice(0, CAP);

  const card = { background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden' };
  const cardHead = { padding: '14px 18px', borderBottom: '1px solid var(--border-default)' };
  const stepNo = { fontSize: 10.5, fontWeight: 700, color: 'var(--text-ghost)', fontFamily: 'monospace' };
  const colHead = { padding: '9px 18px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-subtle)', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: 'var(--text-subtle)' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 18 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Locations</button>
        <div>
          <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--text-default)', letterSpacing: '-.35px' }}>Direct deploy</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 720, marginTop: 2 }}>
            Send devices straight to site without a bench test. Every device is resolved and shown before anything moves,
            and the decision to skip the test is recorded against your name.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 18, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* 01 Destination */}
          <div style={card}>
            <div style={{ ...cardHead, display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span style={stepNo}>01</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-default)' }}>Destination</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>What is already on site decides whether this deployment is safe to make.</div>
              </div>
            </div>

            {destination ? (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 16, padding: '16px 18px', background: 'var(--brand-tint)' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'var(--brand-default)' }} />
                <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-default)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {(destination.customer || locName(destination) || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14 }}>
                    {destination.customer && <span style={{ fontWeight: 700, color: 'var(--brand-default)' }}>{destination.customer} </span>}
                    <span style={{ fontWeight: 600, color: 'var(--text-default)' }}>{locName(destination)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    {[destination.address, destination.siteId && `Site ${destination.siteId}`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ width: 110, flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Devices on site</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-default)', fontFamily: 'monospace' }}>{siteCount(destination)}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => { setLocationId(null); setLocQuery(''); setSubLocation(''); }}>Change</button>
              </div>
            ) : (
              <>
                <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <input className="input" value={locQuery} onChange={e => setLocQuery(e.target.value)}
                    placeholder="Search by site, client, town or site ID" style={{ fontSize: 13.5 }} />
                </div>
                <div style={colHead}>{q ? (matches.length === 1 ? '1 site matches' : `${matches.length} sites match`) : 'Destinations'}</div>
                {shown.map(l => (
                  <div key={l.id} onClick={() => { setLocationId(l.id); setLocQuery(''); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '13px 18px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 24, height: 24, borderRadius: 5, background: 'var(--brand-default)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {(l.customer || locName(l) || '?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5 }}>
                        {l.customer && <span style={{ fontWeight: 700, color: 'var(--brand-default)' }}>{l.customer} </span>}
                        <span style={{ fontWeight: 600, color: 'var(--text-default)' }}>{locName(l)}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 3, alignItems: 'center' }}>
                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                          {[l.address, l.siteId && `Site ${l.siteId}`].filter(Boolean).join(' · ') || '—'}
                        </span>
                        {siteCount(l) === 0 && <span className="pill pill-info" style={{ fontSize: 11 }}>No devices on site yet</span>}
                      </div>
                    </div>
                    <div style={{ width: 110, flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Devices on site</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-default)', fontFamily: 'monospace' }}>{siteCount(l)}</div>
                    </div>
                  </div>
                ))}
                {matches.length > CAP && (
                  <div style={{ padding: '11px 18px', textAlign: 'center', fontSize: 11.5, color: 'var(--text-muted)' }}>
                    {matches.length - CAP} more sites — keep typing to narrow
                  </div>
                )}
                {!matches.length && (
                  <div style={{ padding: '24px 18px', textAlign: 'center', fontSize: 12.5, color: 'var(--text-subtle)' }}>
                    No site matches "{locQuery}".
                  </div>
                )}
              </>
            )}

            {/* Sub-location within the chosen site. Only the manual ones are offered — a
                portal project is assigned by the portal, so picking one here would be
                overwritten on the next sync. A site with none keeps the original flow. */}
            {destination && (() => {
              const subs = subLocationsFor(destination, st).filter(x => x.origin === 'manual');
              if (!subs.length) return null;
              return (
                <div style={{ padding: '13px 18px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-default)' }}>Sub-location</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      Optional — which part of {locName(destination)} these are going to.
                    </div>
                  </div>
                  <select
                    className="input"
                    value={subLocation}
                    onChange={e => setSubLocation(e.target.value)}
                    aria-label="Sub-location"
                    style={{ width: 240, fontSize: 13.5 }}>
                    <option value="">No sub-location</option>
                    {subs.map(x => <option key={x.id} value={x.name}>{x.name}</option>)}
                  </select>
                </div>
              );
            })()}
          </div>

          {/* 02 Devices */}
          <div style={card}>
            <div style={{ ...cardHead, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={stepNo}>02</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-default)' }}>Devices</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>Scan them, or paste a list from the picking sheet. Each serial is resolved against stock as it arrives.</div>
              </div>
              {rows.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-default)', fontFamily: 'monospace' }}>{rows.length}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSerials([]); setReason(null); setReasonNote(''); }}>Clear</button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
              <input ref={scanRef} className="input" value={scanBuf} onChange={e => setScanBuf(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSerials(scanBuf); setScanBuf(''); } }}
                placeholder="Scan a device, or type a serial and press Enter"
                style={{ flex: 1, fontFamily: 'monospace', fontSize: 14 }} />
              <button className="btn btn-ghost" onClick={() => setPasteOpen(o => !o)}>{pasteOpen ? 'Close paste' : 'Paste a list'}</button>
            </div>

            {pasteOpen && (
              <div style={{ padding: '14px 18px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
                  One serial per line, or separated by commas, spaces or semicolons — paste straight from the sheet and it will be split for you.
                </div>
                <textarea className="input" value={pasteBuf} onChange={e => setPasteBuf(e.target.value)}
                  style={{ width: '100%', height: 96, padding: '11px 12px', fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => { addSerials(pasteBuf); setPasteBuf(''); setPasteOpen(false); }}>Add these serials</button>
                  <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Duplicates and serials already in the list are ignored.</span>
                </div>
              </div>
            )}

            {blocked.length > 0 && (() => {
              const byState = {};
              blocked.forEach(r => { byState[r.state] = (byState[r.state] || 0) + 1; });
              const parts = Object.keys(STATES).filter(k => byState[k]).map(k => `${byState[k]} ${STATES[k].phrase}`);
              return (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 18px', background: 'var(--danger-fill)', borderBottom: '1px solid var(--danger-border)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--danger-text)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
                  <div style={{ flex: 1, fontSize: 12.5, color: 'var(--danger-text)', lineHeight: 1.55 }}>
                    {plural(blocked.length, 'device', 'devices')} in this list cannot go to site: {parts.join(', ')}.
                    Deploying the rest is fine — the held-back units stay where they are.
                  </div>
                  {rmaRows.length > 0 && !rmaOverride && (
                    <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0, color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}
                      onClick={() => setRmaOverride(true)}>
                      Override RMA hold ({rmaRows.length})
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0, color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}
                    onClick={() => setSerials(prev => prev.filter(s => !blocks(resolve(s))))}>
                    Remove all {blocked.length}
                  </button>
                </div>
              );
            })()}

            {overriding && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 18px', background: 'var(--warning-fill)', borderBottom: '1px solid var(--warning-border)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--warning-text)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--warning-text)', lineHeight: 1.55 }}>
                    Overriding the RMA hold on {plural(rmaRows.length, 'device', 'devices')}. {rmaRows.length === 1 ? 'It' : 'They'} will be deployed, and a note goes on the RMA record. The RMA itself stays open.
                  </div>
                  <input className="input" value={rmaOverrideNote} onChange={e => setRmaOverrideNote(e.target.value)} autoFocus
                    placeholder="Reason for overriding the RMA hold (recorded)" style={{ marginTop: 8, fontSize: 12.5 }} />
                </div>
                <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}
                  onClick={() => { setRmaOverride(false); setRmaOverrideNote(''); }}>
                  Undo override
                </button>
              </div>
            )}

            {rows.length === 0 ? (
              <div style={{ padding: '36px 18px', textAlign: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 11, background: 'var(--surface-inset)', border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-subtle)" strokeWidth="1.8"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9h10M7 13h6" /></svg>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-default)' }}>No devices added yet</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Scan devices above</div>
              </div>
            ) : (
              <>
                <div style={{ ...colHead, display: 'grid', gridTemplateColumns: '130px minmax(0,1fr) 40px', columnGap: 16 }}>
                  <span>Serial</span><span>State in stock</span><span />
                </div>
                {rows.map((r, i) => {
                  const isOverridden = overriding && r.state === 'rma';
                  const s = isOverridden ? { label: 'RMA overridden', tone: 'warning' } : STATES[r.state], tone = TONE[s.tone];
                  return (
                    <div key={`${r.serial}-${i}`} style={{ display: 'grid', gridTemplateColumns: '130px minmax(0,1fr) 40px', columnGap: 16, alignItems: 'start', padding: '11px 18px', borderBottom: '1px solid var(--border-subtle)', background: tone.row }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-default)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.serial}</div>
                        {r.dev?.type && <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{r.dev.type}</div>}
                      </div>
                      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: tone.fill, color: tone.text, border: `1px solid ${tone.border}` }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />{s.label}
                        </span>
                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{isOverridden ? 'On an open RMA — will be deployed under your override.' : r.why}</span>
                      </div>
                      <button title="Remove from this deployment" className="btn btn-ghost btn-sm" style={{ width: 26, height: 26, padding: 0 }}
                        onClick={() => setSerials(prev => prev.filter((_, ix) => ix !== i))}>×</button>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* Right rail */}
        <div style={{ position: 'sticky', top: 73, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={card}>
            <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border-default)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-default)' }}>What will happen</div>
            {rows.length === 0 ? (
              <div style={{ display: 'flex', gap: 11, padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ width: 18, textAlign: 'center', fontSize: 12.5, fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-subtle)' }}>0</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>Nothing will move until devices are added.</span>
              </div>
            ) : [
              [ready.length, 'var(--success-text)', `${plural(ready.length, 'device has', 'devices have')} a current bench test and will move to site as ready.`],
              [transfers.length, 'var(--info-text)', (() => {
                const from = [...new Set(transfers.map(r => r.from))];
                const where = from.length === 1 ? from[0] : `${from.length} other sites`;
                return `${plural(transfers.length, 'device is', 'devices are')} transferring from ${where} and will be taken off ${from.length === 1 ? 'it' : 'them'}.`;
              })()],
              [untested.length, 'var(--warning-text)', `${plural(untested.length, 'device has', 'devices have')} never been tested and will be marked passed without one.`],
              [overridden.length, 'var(--warning-text)', `${plural(overridden.length, 'device is', 'devices are')} on an open RMA and will move under your override.`],
              [blocked.length, 'var(--danger-text)',`${plural(blocked.length, 'device is', 'devices are')} held back and will not move.`],
            ].filter(([n]) => n > 0).map(([n, c, text], i) => (
              <div key={i} style={{ display: 'flex', gap: 11, padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ width: 18, textAlign: 'center', fontSize: 12.5, fontWeight: 700, fontFamily: 'monospace', color: c }}>{n}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                ['Destination', destination ? `${destination.customer ? destination.customer + ' ' : ''}${locName(destination)}` : 'Not chosen'],
                ['Devices moving', String(movable)],
                ['On site afterwards', destination ? String(siteCount(destination) + movable) : '—'],
                ['Deployed by', account?.name || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-default)', textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {untested.length > 0 && (
            <div style={{ ...card, border: '1px solid var(--warning-border)' }}>
              <div style={{ display: 'flex', gap: 9, padding: '13px 16px', background: 'var(--warning-fill)', borderBottom: '1px solid var(--warning-border)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--warning-text)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--warning-text)' }}>Skipping the bench test</div>
                  <div style={{ fontSize: 11.5, color: 'var(--warning-text)', marginTop: 3, lineHeight: 1.5 }}>
                    {untested.length} of these devices {untested.length === 1 ? 'has' : 'have'} never been through a bench test.
                    Deploying now records {untested.length === 1 ? 'it' : 'them'} as passed on your authority, so the reason has to go on the record.
                  </div>
                </div>
              </div>
              <div style={{ padding: '13px 16px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-body, var(--text-muted))', marginBottom: 7 }}>Reason</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {REASONS.map(rz => {
                    const sel = reason === rz;
                    return (
                      <div key={rz} onClick={() => { setReason(rz); if (rz === 'Other') setTimeout(() => noteRef.current?.focus(), 0); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${sel ? 'var(--brand-border)' : 'var(--border-default)'}`, background: sel ? 'var(--brand-tint)' : 'transparent' }}>
                        <span style={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, border: sel ? 'none' : '1.5px solid var(--border-hover)', background: sel ? 'var(--brand-default)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {sel && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-default)' }}>{rz}</span>
                      </div>
                    );
                  })}
                </div>
                {reason === 'Other' && (
                  <input ref={noteRef} className="input" value={reasonNote} onChange={e => setReasonNote(e.target.value)}
                    placeholder="Name the reason for the record" style={{ marginTop: 8, fontSize: 12.5 }} />
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
                  Recorded as <strong>{account?.name || 'you'}</strong> on {fmtDate(new Date())}, against each device and on the location's history.
                </div>
              </div>
            </div>
          )}

          <div style={card}>
            <div style={{ padding: 16 }}>
              {canDeploy ? (
                <button className="btn btn-primary" style={{ width: '100%', height: 40, fontSize: 13 }} onClick={deploy}>
                  Deploy {plural(movable, 'device', 'devices')} to {locName(destination)} →
                </button>
              ) : (
                <>
                  <div style={{ height: 40, borderRadius: 8, background: 'var(--surface-inset)', border: '1px solid var(--border-default)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 }}>
                    {destination ? `Deploy ${plural(movable, 'device', 'devices')} to ${locName(destination)}` : 'Deploy devices to site'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                    {gates.map(g => (
                      <div key={g} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--warning-text)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                        <span style={{ fontSize: 11.5, color: 'var(--warning-text)', lineHeight: 1.45 }}>{g}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
